import React, { useLayoutEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import SeoGrowthHero from "@/components/public-seo/SeoGrowthHero";
import SeoGrowthFreshness from "@/components/public-seo/SeoGrowthFreshness";
import SeoGrowthFaq from "@/components/public-seo/SeoGrowthFaq";
import SeoGrowthInternalLinks from "@/components/public-seo/SeoGrowthInternalLinks";
import ArticleLeaderboardAd from "@/components/ads/ArticleLeaderboardAd";
import { Button } from "@/components/ui/button";
import { getSeoGrowthPage } from "@/lib/seoGrowthPages";

const SITE_URL = "https://marstick.com";

const hubPathByGroup = {
  "sailing-regulations": "/regulations",
  "marina-comparisons": "/marinas",
  "weather-guides": "/weather",
  "sailing-checklists": "/safety",
  "sailing-locations": "/destinations",
  "nautical-terms": "/glossary",
};

const hubLabelByGroup = {
  "sailing-regulations": "Sailing Regulations",
  "marina-comparisons": "Marinas",
  "weather-guides": "Marine Weather",
  "sailing-checklists": "Safety Checklists",
  "sailing-locations": "Destinations",
  "nautical-terms": "Sailing Glossary",
};

const withCurrentLanguage = (pathname, targetPath) => {
  const lang = pathname.match(/^\/([a-z]{2})(?:\/|$)/)?.[1];
  return lang ? `/${lang}${targetPath}` : targetPath;
};

export default function SeoGrowthPage({ group, slugOverride }) {
  const { slug } = useParams();
  const location = useLocation();
  const page = getSeoGrowthPage(group, slugOverride || slug);

  useLayoutEffect(() => {
    if (!page) return;
    document.title = `${page.title} | Marstick`;
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) { desc = document.createElement("meta"); desc.name = "description"; document.head.appendChild(desc); }
    desc.content = page.description;
    let schema = document.getElementById("seo-growth-schema");
    if (!schema) { schema = document.createElement("script"); schema.id = "seo-growth-schema"; schema.type = "application/ld+json"; document.head.appendChild(schema); }
    const currentUrl = `${SITE_URL}${location.pathname}`;
    const hubLabel = hubLabelByGroup[group] || page.type;
    const hubUrl = `${SITE_URL}${withCurrentLanguage(location.pathname, hubPathByGroup[group] || "/")}`;
    schema.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": [{ "@type": "Article", headline: page.title, description: page.description, dateModified: page.freshness.reviewed, mainEntityOfPage: currentUrl }, { "@type": "FAQPage", mainEntity: page.faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) }, { "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: { "@id": `${SITE_URL}/`, name: "Home" } }, { "@type": "ListItem", position: 2, name: hubLabel, item: { "@id": hubUrl, name: hubLabel } }, { "@type": "ListItem", position: 3, name: page.title, item: { "@id": currentUrl, name: page.title } }] }] });
    return () => schema.remove();
  }, [group, location.pathname, page]);

  if (!page) return <Navigate to="/404" replace />;

  const hubPath = withCurrentLanguage(location.pathname, hubPathByGroup[group] || "/");
  const hubLabel = hubLabelByGroup[group] || page.type;

  return (
    <main className="maritime-page-shell">
      <PublicBreadcrumbs items={[{ label: "Home", href: "/" }, { label: hubLabel, href: hubPath }, { label: page.title }]} />
      <div data-article-intro>
        <SeoGrowthHero page={page} />
      </div>
      <ArticleLeaderboardAd className="my-6" />
      <SeoGrowthFreshness freshness={page.freshness} />
      <section className="rounded-3xl border border-border bg-card p-6 shadow-xl md:p-8">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">Editorial note</p>
        <h2 className="mt-2 font-heading text-2xl font-black">Reviewed for practical skipper use</h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-muted-foreground">
          This guide explains the term in plain language and connects it to safe navigation, voyage planning, watchkeeping, and related Marstick tools.
        </p>
      </section>
      {group === "sailing-checklists" && <Button type="button" className="w-fit" onClick={() => window.print()}>Download / print checklist</Button>}
      <section className="rounded-3xl border border-border bg-card p-6 shadow-xl md:p-8">
        <h2 className="font-heading text-3xl font-black">Practical planning guidance</h2>
        <div className="mt-5 grid gap-4 md:grid-cols-3">{page.sections.map((item) => <p key={item} className="rounded-2xl border border-border bg-background p-4 text-sm leading-7 text-muted-foreground">{item}</p>)}</div>
      </section>
      <SeoGrowthFaq faqs={page.faqs} />
      <SeoGrowthInternalLinks links={page.links} />
    </main>
  );
}